import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { openNotifyModal } from './NotifyMeModal';

export default function TopNavBar({
  lang,
  toggleLanguage,
  setIsModalOpen
}) {
  const {
    t
  } = useTranslation();
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileAccordion, setActiveMobileAccordion] = useState(null);
  const [isScrolled, setIsScrolled] = useState(() => typeof window !== 'undefined' ? window.scrollY > 10 : false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    setActiveMegaMenu(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled((prev) => {
            const next = window.scrollY > 10;
            return prev !== next ? next : prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };
    // Initial check
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <>
      {/* TopNavBar */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? "scrolled top-4 mx-4 rounded-full bg-white shadow-md" : "bg-transparent py-4 w-full"}`} id="main-nav" style={{
      width: isScrolled ? "calc(100% - 32px)" : "100%"
    }}>
        <div className="flex justify-between items-center w-full px-12 h-[72px] max-w-[1440px] mx-auto relative">
          {/* Brand */}
          <Link to="/" className="text-2xl tracking-tighter flex items-center py-1">
            {/* Unscrolled Logo */}
            <img 
              src={process.env.PUBLIC_URL + "/images/ello%20logo%20vertical%20b&w.svg"} 
              alt={t("topnavbar_text_670")} 
              id="logo-default"
              className="h-16 sm:h-20 lg:h-20 xl:h-[90px] w-auto max-w-[70vw] sm:max-w-[300px] md:max-w-[360px] xl:max-w-[480px] transition-all duration-300 object-contain hover:scale-105 filter drop-shadow-[0_4px_12px_rgba(0,0,0,0.85)] brightness-105"
            />
            
            {/* Scrolled Logo (Exact Image Layout) */}
            <img 
              src={process.env.PUBLIC_URL + "/images/ello%20logo%20horiz%20b&w.svg"} 
              alt={t("topnavbar_text_670")} 
              id="logo-scrolled"
              className="h-10 sm:h-12 md:h-14 lg:h-14 xl:h-[64px] w-auto max-w-[70vw] sm:max-w-[280px] md:max-w-[320px] xl:max-w-[400px] transition-all duration-300 object-contain hover:scale-105 filter drop-shadow-[0_2px_8px_rgba(0,0,0,0.15)] brightness-105"
            />
          </Link>
          {/* Navigation Links */}
          <div className="hidden lg:flex items-center space-x-8">
            <div className="cursor-pointer font-manrope text-sm tracking-wide pb-4 -mb-4 pt-4 -mt-4" onMouseEnter={() => setActiveMegaMenu('products')} onMouseLeave={() => setActiveMegaMenu(null)}>
              <span className="nav-link inline-flex transition-all duration-300 items-center gap-1 font-bold relative whitespace-nowrap">
                <span dangerouslySetInnerHTML={{
                __html: t("topnavbar_span_671")
              }} />
                <span className={`material-symbols-outlined text-xs transition-transform duration-300 ${activeMegaMenu === 'products' ? 'rotate-180' : ''}`}>{t("topnavbar_text_672")}</span>
              </span>
              <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max max-w-[95vw] bg-surface-container-lowest rounded-3xl shadow-[0_24px_48px_-12px_rgba(28,27,34,0.15)] border border-outline-variant/20 transition-all duration-300 transform z-50 overflow-hidden ${activeMegaMenu === 'products' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'}`} onClick={() => setActiveMegaMenu(null)}>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                  {/* Replacement */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => navigate('/products/replacement-insurance')}>
                    <span className="absolute -top-1.5 -right-2 whitespace-normal bg-gradient-to-r from-[#B8860B] to-[#D4AF37] text-white text-[5.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shadow-[0_2px_10px_rgba(212,175,55,0.4)] z-20 animate-breath opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_673")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">directions_car</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_674")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_675")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/products/replacement-insurance" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_676")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                          navigate('/products/replacement-insurance/quote');
                          window.scrollTo(0, 0);
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_677")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Travel */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_678")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">flight_takeoff</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_679")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_680")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_681")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                            openNotifyModal();
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_682")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Cyber */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_683")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">security</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_684")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_685")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_686")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                            openNotifyModal();
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_687")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Boat */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_688")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">sailing</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_689")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_690")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_691")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                            openNotifyModal();
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_692")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Event */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_693")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">celebration</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_694")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_695")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_696")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                            openNotifyModal();
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_697")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sports */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_698")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">emoji_events</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_699")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_700")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_701")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                            openNotifyModal();
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_702")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RBQ */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_703")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">gavel</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_704")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_705")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_706")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-3 py-1.5 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                            openNotifyModal();
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_707")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Claims Mega Menu */}
            <div className="cursor-pointer font-manrope text-sm tracking-wide pb-4 -mb-4 pt-4 -mt-4" onMouseEnter={() => setActiveMegaMenu('claims')} onMouseLeave={() => setActiveMegaMenu(null)}>
              <span className="nav-link inline-flex transition-all duration-300 items-center gap-1 font-bold relative whitespace-nowrap">
                <span dangerouslySetInnerHTML={{
                __html: t("topnavbar_span_708")
              }} />
                <span className={`material-symbols-outlined text-xs transition-transform duration-300 ${activeMegaMenu === 'claims' ? 'rotate-180' : ''}`}>{t("topnavbar_text_709")}</span>
              </span>
              <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max max-w-[95vw] bg-surface-container-lowest rounded-3xl shadow-[0_24px_48px_-12px_rgba(28,27,34,0.15)] border border-outline-variant/20 transition-all duration-300 transform z-50 overflow-hidden ${activeMegaMenu === 'claims' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'}`} onClick={() => setActiveMegaMenu(null)}>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                  {/* Replacement Insurance */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => navigate('/claims/replacement-insurance')}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">directions_car</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_710")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_711")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/claims/replacement-insurance" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_712")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                          <Link to="/claims/replacement-insurance/register" className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95" onClick={e => {
                          e.stopPropagation();
                          window.scrollTo(0, 0);
                        }}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_713")
                          }} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Travel Insurance */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_714")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">flight_takeoff</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_715")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_716")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_717")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_718")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cyber Insurance */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_719")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">security</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_720")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_721")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_722")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_723")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boat Insurance */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_724")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">sailing</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_725")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_726")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_727")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_728")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Event Insurance */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_729")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">celebration</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_730")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_731")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_732")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_733")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sports Insurance */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_734")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">emoji_events</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_735")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_736")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_737")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_738")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RBQ Surety Bonds */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[5px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1 h-1 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{
                      __html: t("topnavbar_span_739")
                    }} />
                      
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">gavel</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_740")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_741")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-[10px] cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_742")
                          }} />
                            <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-[10px] px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_743")
                          }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Blogs Mega Menu */}
            <div className="cursor-pointer font-manrope text-sm tracking-wide pb-4 -mb-4 pt-4 -mt-4" onMouseEnter={() => setActiveMegaMenu('blogs')} onMouseLeave={() => setActiveMegaMenu(null)}>
              <span className="nav-link inline-flex transition-all duration-300 items-center gap-1 font-bold relative whitespace-nowrap">
                <span dangerouslySetInnerHTML={{
                __html: t("topnavbar_span_744")
              }} />
                <span className={`material-symbols-outlined text-xs transition-transform duration-300 ${activeMegaMenu === 'blogs' ? 'rotate-180' : ''}`}>{t("topnavbar_text_745")}</span>
              </span>
              <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max max-w-[95vw] bg-surface-container-lowest rounded-3xl shadow-[0_24px_48px_-12px_rgba(28,27,34,0.15)] border border-outline-variant/20 transition-all duration-300 transform z-50 overflow-hidden ${activeMegaMenu === 'blogs' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'}`} onClick={() => setActiveMegaMenu(null)}>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                  {/* All Articles */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/blogs');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">article</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_746")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_747")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_748")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Insurance Tips */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/blogs?category=tips');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">lightbulb</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_749")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_750")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs?category=tips" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_751")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* News & Updates */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/blogs?category=news');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">newspaper</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_752")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_753")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs?category=news" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_754")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Comprehensive Guides */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/blogs?category=guides');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">menu_book</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_755")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_756")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs?category=guides" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_757")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQs Mega Menu */}
            <div className="cursor-pointer font-manrope text-sm tracking-wide pb-4 -mb-4 pt-4 -mt-4" onMouseEnter={() => setActiveMegaMenu('faqs')} onMouseLeave={() => setActiveMegaMenu(null)}>
              <span className="nav-link inline-flex transition-all duration-300 items-center gap-1 font-bold relative whitespace-nowrap">
                <span dangerouslySetInnerHTML={{
                __html: t("topnavbar_span_758")
              }} />
                <span className={`material-symbols-outlined text-xs transition-transform duration-300 ${activeMegaMenu === 'faqs' ? 'rotate-180' : ''}`}>{t("topnavbar_text_759")}</span>
              </span>
              <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max max-w-[95vw] bg-surface-container-lowest rounded-3xl shadow-[0_24px_48px_-12px_rgba(28,27,34,0.15)] border border-outline-variant/20 transition-all duration-300 transform z-50 overflow-hidden ${activeMegaMenu === 'faqs' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'}`} onClick={() => setActiveMegaMenu(null)}>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                  {/* General */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/faqs?category=general');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">quiz</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_760")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_761")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=general" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_762")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Policy & Coverage */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/faqs?category=product');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">policy</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_763")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_764")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=product" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_765")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Billing & Payment */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/faqs?category=billing');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">payments</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_766")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_767")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=billing" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_768")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Claims */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/faqs?category=claims');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">support_agent</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_769")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_770")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=claims" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_771")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Mega Menu */}
            <div className="cursor-pointer font-manrope text-sm tracking-wide pb-4 -mb-4 pt-4 -mt-4" onMouseEnter={() => setActiveMegaMenu('company')} onMouseLeave={() => setActiveMegaMenu(null)}>
              <span className="nav-link inline-flex transition-all duration-300 items-center gap-1 font-bold relative whitespace-nowrap">
                <span dangerouslySetInnerHTML={{
                __html: t("topnavbar_span_772")
              }} />
                <span className={`material-symbols-outlined text-xs transition-transform duration-300 ${activeMegaMenu === 'company' ? 'rotate-180' : ''}`}>{t("topnavbar_text_773")}</span>
              </span>
              <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max max-w-[95vw] bg-surface-container-lowest rounded-3xl shadow-[0_24px_48px_-12px_rgba(28,27,34,0.15)] border border-outline-variant/20 transition-all duration-300 transform z-50 overflow-hidden ${activeMegaMenu === 'company' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'}`} onClick={() => setActiveMegaMenu(null)}>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                  {/* About Us */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/about-us#story');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">history_edu</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_774")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_775")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/about-us#story" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_776")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Contact Us */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/contact-us');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">mail</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_777")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_778")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/contact-us" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_779")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Partner with Us */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/partner-with-us');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">handshake</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_780")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_781")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/partner-with-us" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_782")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Compliments & Complaints */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-xl hover:bg-surface-container-low transition-colors group/item relative h-full cursor-pointer" onClick={() => {
                  navigate('/compliments-and-complaints');
                  window.scrollTo(0, 0);
                }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-full bg-surface-container flex items-center justify-center shrink-0 group-hover/item:bg-primary-fixed-dim transition-colors">
                        <span className="material-symbols-outlined text-primary">forum</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_783")
                        }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{
                          __html: t("topnavbar_span_784")
                        }} />
                          
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/compliments-and-complaints" className="inline-flex items-center gap-1 text-primary hover:text-tertiary font-bold group/link text-[10px]" onClick={() => window.scrollTo(0, 0)}>
                            <span dangerouslySetInnerHTML={{
                            __html: t("topnavbar_span_785")
                          }} />
                            <span className="material-symbols-outlined text-[10px] group-hover/link:translate-x-1 transition-transform duration-300">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            <a href="tel:+15143645715" className={`flex items-center justify-center gap-2 font-manrope text-sm font-bold transition-all duration-300 rounded-full border cursor-pointer ${isScrolled ? 'border-primary/20 text-primary hover:bg-primary hover:text-white shadow-sm' : 'border-white/30 text-white hover:bg-white hover:text-primary shadow-[0_0_15px_rgba(255,255,255,0.1)]'} h-10 w-10 xl:w-auto xl:px-4`}>
              <span className="material-symbols-outlined text-base">call</span>
              <span className="hidden xl:block whitespace-nowrap">+1 514 364 5715</span>
            </a>
            <div className="flex items-center">
              <label className="inline-flex items-center cursor-pointer">
                <span className={`mr-2 text-xs font-bold transition-all duration-300 ${lang === 'fr' ? isScrolled ? 'text-primary' : 'text-white' : isScrolled ? 'text-slate-500' : 'text-white/60'}`}>{t("topnavbar_text_787")}</span>
                <div className="relative">
                  <input className="sr-only peer lang-toggle-checkbox" onChange={toggleLanguage} checked={lang === 'en'} type="checkbox" />
                  <div className={`w-9 h-5 ${isScrolled ? 'bg-slate-200' : 'bg-white/20'} border border-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[100%] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all ${isScrolled ? 'peer-checked:bg-primary' : 'peer-checked:bg-white/40'} nav-toggle-track`}></div>
                </div>
                <span className={`ml-2 text-xs font-bold transition-all duration-300 ${lang === 'en' ? isScrolled ? 'text-primary' : 'text-white' : isScrolled ? 'text-slate-500' : 'text-white/60'}`}>{t("topnavbar_text_786")}</span>
              </label>
            </div>
            <button className="btn-primary text-sm px-6 py-3 rounded-full" id="header-quote-btn" onClick={() => {
            setIsModalOpen(true);
            document.body.style.overflow = "hidden";
          }}>
              <span dangerouslySetInnerHTML={{
              __html: t("topnavbar_span_788")
            }} />
            </button>
          </div>
          {/* Mobile Menu Toggle */}
          <button className={`lg:hidden ${isScrolled ? "text-on-surface" : "text-white"}`} onClick={() => {
          const newState = !isMobileMenuOpen;
          setIsMobileMenuOpen(newState);
          document.body.style.overflow = newState ? "hidden" : "auto";
        }}>
            <span className="material-symbols-outlined text-3xl">{isMobileMenuOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-[100] bg-surface/95 backdrop-blur-xl lg:hidden transition-all duration-500 flex flex-col ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="flex items-center justify-between p-6 border-b border-outline-variant/10">
          <Link to="/" onClick={() => {
          setIsMobileMenuOpen(false);
          document.body.style.overflow = "auto";
        }}>
            <img src={process.env.PUBLIC_URL + "/images/ello%20logo%20horiz%20b&w.svg"} alt={t("topnavbar_attr_789")} className="h-10 w-auto object-contain max-w-[70vw] filter drop-shadow-[0_2px_8px_rgba(0,0,0,0.15)] brightness-105" />
          </Link>
          <button onClick={() => {
          setIsMobileMenuOpen(false);
          document.body.style.overflow = "auto";
        }} className="w-12 h-12 rounded-full bg-surface-container-high flex items-center justify-center text-on-surface">
            <span className="material-symbols-outlined text-3xl">close</span>
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-6 py-12 flex flex-col items-center gap-10">
          <nav className="flex flex-col items-center gap-6 w-full">
            <Link to="/" onClick={() => {
            setIsMobileMenuOpen(false);
            document.body.style.overflow = "auto";
          }} className="text-2xl font-headline font-black text-on-surface hover:text-primary transition-colors py-2">
              <span dangerouslySetInnerHTML={{
              __html: t("topnavbar_span_790")
            }} />
            </Link>

            {/* 1. Products Accordion */}
            <div className="w-full flex flex-col items-center">
              <button 
                className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all duration-300 ${activeMobileAccordion === 'products' ? 'bg-surface-container border border-outline-variant/20 shadow-sm' : 'bg-transparent hover:bg-surface-container-low'}`}
                onClick={() => setActiveMobileAccordion(activeMobileAccordion === 'products' ? null : 'products')}
              >
                <span className="text-2xl font-headline font-black text-on-surface">
                  <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_671") }} />
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeMobileAccordion === 'products' ? 'bg-primary text-white rotate-180' : 'bg-surface-container-high text-on-surface'}`}>
                  <span className="material-symbols-outlined">expand_more</span>
                </div>
              </button>
              
              <div className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${activeMobileAccordion === 'products' ? 'max-h-[3000px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
                <div className="flex flex-col gap-4 px-2 pb-4 text-left">
                  {/* Replacement */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative group/item cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/products/replacement-insurance'); }}>
                    <span className="absolute -top-1.5 -right-2 whitespace-normal bg-gradient-to-r from-[#B8860B] to-[#D4AF37] text-white text-[7px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full shadow-[0_2px_10px_rgba(212,175,55,0.4)] z-20 animate-breath opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_673") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">directions_car</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_674") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_675") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/products/replacement-insurance" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_676") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full shadow-md" onClick={e => {
                            e.stopPropagation();
                            setIsMobileMenuOpen(false);
                            document.body.style.overflow = "auto";
                            navigate('/products/replacement-insurance/quote');
                            window.scrollTo(0, 0);
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_677") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Travel */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_678") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">flight_takeoff</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_679") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_680") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_681") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-pointer shadow-md" onClick={e => {
                            e.stopPropagation();
                              openNotifyModal();
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_682") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cyber */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_683") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">security</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_684") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_685") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_686") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-pointer shadow-md" onClick={e => {
                            e.stopPropagation();
                              openNotifyModal();
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_687") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boat */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_688") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">sailing</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_689") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_690") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_691") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-pointer shadow-md" onClick={e => {
                            e.stopPropagation();
                              openNotifyModal();
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_692") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Event */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_693") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">celebration</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_694") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_695") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_696") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-pointer shadow-md" onClick={e => {
                            e.stopPropagation();
                              openNotifyModal();
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_697") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sports */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_698") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">emoji_events</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_699") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_700") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_701") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-pointer shadow-md" onClick={e => {
                            e.stopPropagation();
                              openNotifyModal();
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_702") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RBQ Surety Bonds */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_703") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">gavel</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_704") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_705") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed opacity-50">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_706") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-pointer shadow-md" onClick={e => {
                            e.stopPropagation();
                              openNotifyModal();
                          }}>
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_707") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Claims Accordion */}
            <div className="w-full flex flex-col items-center">
              <button 
                className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all duration-300 ${activeMobileAccordion === 'claims' ? 'bg-surface-container border border-outline-variant/20 shadow-sm' : 'bg-transparent hover:bg-surface-container-low'}`}
                onClick={() => setActiveMobileAccordion(activeMobileAccordion === 'claims' ? null : 'claims')}
              >
                <span className="text-2xl font-headline font-black text-on-surface">
                  <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_708") }} />
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeMobileAccordion === 'claims' ? 'bg-primary text-white rotate-180' : 'bg-surface-container-high text-on-surface'}`}>
                  <span className="material-symbols-outlined">expand_more</span>
                </div>
              </button>
              
              <div className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${activeMobileAccordion === 'claims' ? 'max-h-[3000px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
                <div className="flex flex-col gap-4 px-2 pb-4 text-left">
                  {/* Replacement Insurance Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative group/item cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/claims/replacement-insurance'); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">directions_car</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_710") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_711") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/claims/replacement-insurance" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_712") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                          <Link to="/claims/replacement-insurance/register" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="btn-primary text-xs px-4 py-2 rounded-full shadow-md">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_713") }} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Travel Insurance Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_714") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">flight_takeoff</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_715") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_716") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_717") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_718") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cyber Insurance Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_719") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">security</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_720") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_721") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_722") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_723") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boat Insurance Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_724") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">sailing</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_725") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_726") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_727") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_728") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Event Insurance Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_729") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">celebration</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_730") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_731") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_732") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_733") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sports Insurance Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_734") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">emoji_events</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_735") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_736") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_737") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_738") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RBQ Surety Bonds Claim */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest/70 border border-outline-variant/10 relative group/item opacity-90 shadow-sm">
                    <span className="absolute -top-1.5 -right-2 flex items-center whitespace-normal gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-600 text-white text-[7px] font-black uppercase tracking-[0.15em] shadow-lg z-20 opacity-0 group-hover/item:opacity-100 scale-95 group-hover/item:scale-100 transition-all duration-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-pulse"></span>
                      <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_739") }} />
                    </span>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">gavel</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_740") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_741") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3 opacity-50">
                          <div className="inline-flex items-center gap-1 text-primary font-bold text-xs cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_742") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </div>
                          <div className="btn-primary text-xs px-4 py-2 rounded-full cursor-not-allowed">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_743") }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Blogs Accordion */}
            <div className="w-full flex flex-col items-center">
              <button 
                className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all duration-300 ${activeMobileAccordion === 'blogs' ? 'bg-surface-container border border-outline-variant/20 shadow-sm' : 'bg-transparent hover:bg-surface-container-low'}`}
                onClick={() => setActiveMobileAccordion(activeMobileAccordion === 'blogs' ? null : 'blogs')}
              >
                <span className="text-2xl font-headline font-black text-on-surface">
                  <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_744") }} />
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeMobileAccordion === 'blogs' ? 'bg-primary text-white rotate-180' : 'bg-surface-container-high text-on-surface'}`}>
                  <span className="material-symbols-outlined">expand_more</span>
                </div>
              </button>
              
              <div className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${activeMobileAccordion === 'blogs' ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
                <div className="flex flex-col gap-4 px-2 pb-4 text-left">
                  {/* All Articles */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/blogs'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">article</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_746") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_747") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_748") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Insurance Tips */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/blogs?category=tips'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">lightbulb</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_749") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_750") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs?category=tips" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_751") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* News & Updates */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/blogs?category=news'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">newspaper</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_752") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_753") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs?category=news" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_754") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comprehensive Guides */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/blogs?category=guides'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">menu_book</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_755") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_756") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/blogs?category=guides" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_757") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. FAQs Accordion */}
            <div className="w-full flex flex-col items-center">
              <button 
                className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all duration-300 ${activeMobileAccordion === 'faqs' ? 'bg-surface-container border border-outline-variant/20 shadow-sm' : 'bg-transparent hover:bg-surface-container-low'}`}
                onClick={() => setActiveMobileAccordion(activeMobileAccordion === 'faqs' ? null : 'faqs')}
              >
                <span className="text-2xl font-headline font-black text-on-surface">
                  <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_758") }} />
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeMobileAccordion === 'faqs' ? 'bg-primary text-white rotate-180' : 'bg-surface-container-high text-on-surface'}`}>
                  <span className="material-symbols-outlined">expand_more</span>
                </div>
              </button>
              
              <div className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${activeMobileAccordion === 'faqs' ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
                <div className="flex flex-col gap-4 px-2 pb-4 text-left">
                  {/* General FAQ */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/faqs?category=general'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">quiz</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_760") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_761") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=general" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_762") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Policy & Coverage FAQ */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/faqs?category=product'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">policy</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_763") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_764") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=product" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_765") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Billing & Payment FAQ */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/faqs?category=billing'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">payments</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_766") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_767") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=billing" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_768") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Claims FAQ */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/faqs?category=claims'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">support_agent</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_769") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_770") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/faqs?category=claims" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_771") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Company Accordion */}
            <div className="w-full flex flex-col items-center">
              <button 
                className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all duration-300 ${activeMobileAccordion === 'company' ? 'bg-surface-container border border-outline-variant/20 shadow-sm' : 'bg-transparent hover:bg-surface-container-low'}`}
                onClick={() => setActiveMobileAccordion(activeMobileAccordion === 'company' ? null : 'company')}
              >
                <span className="text-2xl font-headline font-black text-on-surface">
                  <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_772") }} />
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeMobileAccordion === 'company' ? 'bg-primary text-white rotate-180' : 'bg-surface-container-high text-on-surface'}`}>
                  <span className="material-symbols-outlined">expand_more</span>
                </div>
              </button>
              
              <div className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${activeMobileAccordion === 'company' ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
                <div className="flex flex-col gap-4 px-2 pb-4 text-left">
                  {/* About Us */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/about-us#story'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">history_edu</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_774") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_775") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/about-us#story" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_776") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Us */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/contact-us'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">mail</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_777") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_778") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/contact-us" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_779") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Partner with Us */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/partner-with-us'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">handshake</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_780") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_781") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/partner-with-us" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_782") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Compliments & Complaints */}
                  <div className="flex flex-col items-start gap-4 p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 relative cursor-pointer shadow-sm hover:border-primary/30 transition-all" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; navigate('/compliments-and-complaints'); window.scrollTo(0, 0); }}>
                    <div className="flex items-start gap-4 w-full">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-primary">forum</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-headline font-bold text-primary mb-1 text-base"><span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_783") }} /></h4>
                        <p className="font-body text-xs text-on-surface-variant leading-relaxed pr-0 text-left">
                          <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_784") }} />
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-3">
                          <Link to="/compliments-and-complaints" onClick={() => { setIsMobileMenuOpen(false); document.body.style.overflow = "auto"; }} className="inline-flex items-center gap-1 text-primary font-bold text-xs">
                            <span dangerouslySetInnerHTML={{ __html: t("topnavbar_span_785") }} />
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>

          <div className="w-full h-px bg-outline-variant/20"></div>

          <div className="flex flex-col items-center gap-6 w-full pb-8">
            <label className="inline-flex items-center cursor-pointer bg-surface-container-high px-6 py-3 rounded-full border border-outline-variant/30">
              <span className={`mr-4 text-sm font-bold ${lang === 'fr' ? 'text-primary' : 'text-on-surface-variant'}`}>{t("topnavbar_text_787")}</span>
              <div className="relative">
                <input className="sr-only peer lang-toggle-checkbox" onChange={toggleLanguage} checked={lang === 'en'} type="checkbox" />
                <div className="w-11 h-6 bg-surface-container-highest border border-outline-variant/30 rounded-full peer peer-checked:after:translate-x-[100%] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </div>
              <span className={`ml-4 text-sm font-bold ${lang === 'en' ? 'text-primary' : 'text-on-surface-variant'}`}>{t("topnavbar_text_786")}</span>
            </label>

            <button className="w-full btn-primary py-4 rounded-2xl text-base font-bold shadow-xl flex items-center justify-center gap-2" onClick={() => {
            setIsMobileMenuOpen(false);
            setIsModalOpen(true);
            document.body.style.overflow = "hidden";
          }}>
              <span dangerouslySetInnerHTML={{
              __html: t("topnavbar_span_788")
            }} />
              <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </button>

            <a href="tel:+15143645715" className="flex items-center gap-3 text-on-surface hover:text-primary font-bold transition-colors py-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary">call</span>
              </div>
              <span className="text-base">+1 514 364 5715</span>
            </a>
          </div>
        </div>
      </div>

    </>;
}
